<script lang="ts">
import { defineComponent, PropType } from "vue";
import { useI18n } from "vue-i18n";
import FilterGroup from "./util/FilterGroup.vue";
import Magnifier from "./util/Magnifier.vue";
import type { SpellType } from "@interfaces/spell_type";
import type { RosterType } from "@interfaces/roster_type";
import type { StructureType } from "@interfaces/structure_type";
import { resolveEntityImageUrl } from "../../../utils/entityImageUrl";

export default defineComponent({
  name: "SelectSpell",
  props: {
    roster: {
      type: Object as PropType<RosterType>,
      required: true,
    },
    structure: {
      type: Object as PropType<StructureType>,
      required: true,
    },
  },
  components: {
    FilterGroup,
    Magnifier,
  },
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      max_spells: 10,
      filter: [] as string[],
    };
  },
  computed: {
    available_spells(): SpellType[] {
      return this.structure.hero?.available_spells?.sort((a, b) => a.weight - b.weight) || [];
    },
    filtered_spells(): SpellType[] {
      if (this.filter.length === 0) return this.available_spells;
      return this.available_spells.filter((spell) =>
        this.spellDomains(spell).some((domain) => this.filter.includes(domain))
      );
    },
    selected_spells(): SpellType[] {
      return this.structure.spells;
    },
    selectedQuantity() {
      return this.selected_spells.reduce(
        (partialSum, spell) => partialSum + spell.quantity,
        0
      );
    },
    currentDeck(): SpellType[] {
      // Create individual spell cards from selected spells
      const spellCards = this.selected_spells.flatMap(spell =>
        Array(spell.quantity).fill({...spell, quantity: 1})
      );

      // Fill remaining deck slots with placeholder cards
      const placeholderCards = Array(Math.max(0, this.max_spells - spellCards.length))
        .fill({
          image_path: resolveEntityImageUrl("/images/spells/card_back.webp"),
          virtual: true
        } as SpellType);

      return [...spellCards, ...placeholderCards];
    },
    availableFilters() {
      return [...new Set(this.available_spells.flatMap((spell) => this.spellDomains(spell)))];
    },
  },
  methods: {
    spellDomains(spell: SpellType): string[] {
      return spell.domain_names?.length ? spell.domain_names : (spell.domain_name || '').split(/[\s,]+/).filter(Boolean);
    },
    spell_available(spell: SpellType) {
      const target = this.selected_spells.find(
        (item) => item.tech_name === spell.tech_name
      );
      return spell.quantity - (target ? target.quantity : 0);
    },
    add_spell(spell: SpellType) {
      if (this.selectedQuantity >= this.max_spells) return;

      const selected_spell = this.selected_spells.find(
        (item) => item.tech_name === spell.tech_name
      );
      const available_spell = this.available_spells.find(
        (item) => item.tech_name === spell.tech_name
      );

      const selectedQty = selected_spell?.quantity ?? 0;
      const availableQty = available_spell?.quantity ?? 0;
      if (selectedQty >= availableQty) return;

      if (selected_spell) {
        selected_spell.quantity++;
      } else {
        this.selected_spells.push({ ...spell, quantity: 1 });
      }
    },
    subtract_spell(spell: SpellType) {
      if (spell.quantity === 0) return;

      const selectedSpell = this.selected_spells.find(
        item => item.tech_name === spell.tech_name
      );

      if (!selectedSpell || selectedSpell.quantity <= 0) return;

      selectedSpell.quantity--;

      if (selectedSpell.quantity === 0) {
        const index = this.selected_spells.indexOf(selectedSpell);
        this.selected_spells.splice(index, 1);
      }
    },
    startDrag(event: DragEvent, item: SpellType, type: "add" | "remove") {
      const dt = event.dataTransfer;
      if (!dt) return;

      dt.dropEffect = "move";
      dt.effectAllowed = "move";
      dt.setData("tech_name", item.tech_name ?? "");

      if (type == "add") {
        dt.setData("type", "add");
      } else {
        dt.setData("type", "remove");
      }
    },
    cloneSpell(event: DragEvent) {
      const dt = event.dataTransfer;
      if (!dt) return;

      const type = dt.getData("type");
      if (type == "remove") return;

      const tech_name = dt.getData("tech_name");
      const spell = this.available_spells.find((item) => item.tech_name === tech_name);

      if (!spell) return;
      this.add_spell(spell);
    },
    removeSpell(event: DragEvent) {
      const dt = event.dataTransfer;
      if (!dt) return;

      const type = dt.getData("type");
      if (type == "add") return;

      const tech_name = dt.getData("tech_name");
      const spell = this.selected_spells.find((item) => item.tech_name === tech_name);

      if (spell) {
        this.subtract_spell(spell);
      }
    },
  },
});
</script>

<template>
  <div class="spells">
    <div
      class="spells-selected shadow-sm"
      @drop="cloneSpell($event)"
      @dragover.prevent
      @dragenter.prevent
    >
      <template v-for="spell in currentDeck">
        <div class="spell disabled" v-if="spell.virtual">
          <div>
            <img :src="spell.image_path" class="image rounded mx-auto d-block" />
          </div>
        </div>

        <div class="spell" v-else>
          <div
            draggable
            @click="subtract_spell(spell)"
            @dragstart="startDrag($event, spell, 'remove')"
          >
            <img
              v-image-fallback
              :src="spell.image_path"
              :alt="spell.tech_name ?? undefined"
              class="image rounded mx-auto d-block"
            />
          </div>
        </div>
      </template>
    </div>

    <div
      class="spells-available shadow-sm"
      @drop="removeSpell($event)"
      @dragover.prevent
      @dragenter.prevent
    >
      <filter-group :filter="filter" :available_filters="availableFilters" />

      <TransitionGroup name="spells">
        <div
          class="spell shadow-sm"
          draggable
          @dragstart="startDrag($event, spell, 'add')"
          @click="add_spell(spell)"
          :class="{
            disabled: spell_available(spell) == 0 || selectedQuantity == max_spells,
          }"
          v-for="spell in filtered_spells"
          :key="spell.id ?? spell.tech_name ?? spell.image_path"
        >
          <img
            v-image-fallback
            :src="spell.image_path"
            :alt="spell.tech_name ?? undefined"
            class="image rounded mx-auto d-block"
          />

          <p class="quantity">
            <b>{{ t('spells.available') }}&nbsp;
              <span>{{ spell_available(spell) }}</span>
            </b>
          </p>

          <magnifier :target="spell"></magnifier>
        </div>
      </TransitionGroup>
    </div>
  </div>
</template>

<style>
.spells .spell img.magnifier {
  width: 50px;
  height: 50px;

  position: relative;
  top: -110px;
}
</style>
